import { apiJson } from '@/lib/api';
import type {
  PortalAssetSignResponse,
  PortalAssetSignVariant,
  PortalCampaignListResponse,
  PortalCampaignProgressResponse,
  PortalCampaignReportDownloadResponse,
  PortalCampaignSummary,
  PortalCampaignWorkspaceResponse,
  PortalCockpitSummaryResponse,
  PortalComplianceSummaryResponse,
  PortalContextResponse,
  PortalCycleSummary,
  PortalGalleryResponse,
  PortalStoreListResponse,
} from '@shared/portal-api-types';

export function fetchPortalContext(): Promise<PortalContextResponse> {
  return apiJson<PortalContextResponse>('/api/portal/context');
}

export function fetchPortalCycles(): Promise<{ items: PortalCycleSummary[] }> {
  return apiJson<{ items: PortalCycleSummary[] }>('/api/portal/cycles');
}

export function fetchPortalStores(options?: {
  search?: string;
  page?: number;
  pageSize?: number;
  retailerId?: number;
}): Promise<PortalStoreListResponse> {
  const params = new URLSearchParams();
  if (options?.search?.trim()) params.set('search', options.search.trim());
  if (options?.page) params.set('page', String(options.page));
  if (options?.pageSize) params.set('pageSize', String(options.pageSize));
  if (options?.retailerId) params.set('retailerId', String(options.retailerId));
  const query = params.toString();
  return apiJson<PortalStoreListResponse>(`/api/portal/stores${query ? `?${query}` : ''}`);
}

export function fetchPortalCampaigns(
  cycleId: number,
  options?: { search?: string; page?: number; pageSize?: number },
): Promise<PortalCampaignListResponse> {
  const params = new URLSearchParams({ cycleId: String(cycleId) });
  if (options?.search?.trim()) params.set('search', options.search.trim());
  if (options?.page) params.set('page', String(options.page));
  if (options?.pageSize) params.set('pageSize', String(options.pageSize));
  return apiJson<PortalCampaignListResponse>(`/api/portal/campaigns?${params.toString()}`);
}

export async function fetchAllPortalCampaigns(cycleId: number): Promise<PortalCampaignSummary[]> {
  const pageSize = 100;
  let page = 1;
  const items: PortalCampaignSummary[] = [];
  let totalCount = 0;

  do {
    const response = await fetchPortalCampaigns(cycleId, { page, pageSize });
    totalCount = response.totalCount;
    items.push(...response.items);
    if (response.items.length === 0) break;
    page += 1;
  } while (items.length < totalCount);

  return items;
}

export function fetchPortalCampaignProgress(
  campaignId: number,
  cycleId: number,
): Promise<PortalCampaignProgressResponse> {
  const params = new URLSearchParams({ cycleId: String(cycleId) });
  return apiJson<PortalCampaignProgressResponse>(
    `/api/portal/campaigns/${campaignId}/progress?${params.toString()}`,
  );
}

export function fetchPortalCampaignWorkspace(
  campaignId: number,
  cycleId: number,
): Promise<PortalCampaignWorkspaceResponse> {
  const params = new URLSearchParams({ cycleId: String(cycleId) });
  return apiJson<PortalCampaignWorkspaceResponse>(
    `/api/portal/campaigns/${campaignId}/workspace?${params.toString()}`,
  );
}

export function fetchPortalCampaignReportDownload(
  campaignId: number,
  reportId: number,
): Promise<PortalCampaignReportDownloadResponse> {
  return apiJson<PortalCampaignReportDownloadResponse>(
    `/api/portal/campaigns/${campaignId}/reports/${reportId}/download`,
  );
}

export function fetchPortalCockpit(cycleId: number): Promise<PortalCockpitSummaryResponse> {
  const params = new URLSearchParams({ cycleId: String(cycleId) });
  return apiJson<PortalCockpitSummaryResponse>(`/api/portal/cockpit?${params.toString()}`);
}

export function fetchPortalCompliance(filters: {
  cycleId: number;
  campaignId?: number;
}): Promise<PortalComplianceSummaryResponse> {
  const params = new URLSearchParams({ cycleId: String(filters.cycleId) });
  if (filters.campaignId) params.set('campaignId', String(filters.campaignId));
  return apiJson<PortalComplianceSummaryResponse>(`/api/portal/compliance?${params.toString()}`);
}

export function fetchPortalGallery(filters: {
  cycleId?: number;
  campaignId?: number;
  storeId?: number;
  retailerId?: number;
  search?: string;
  page?: number;
  pageSize?: number;
}): Promise<PortalGalleryResponse> {
  const params = new URLSearchParams();
  if (filters.cycleId) params.set('cycleId', String(filters.cycleId));
  if (filters.campaignId) params.set('campaignId', String(filters.campaignId));
  if (filters.storeId) params.set('storeId', String(filters.storeId));
  if (filters.retailerId) params.set('retailerId', String(filters.retailerId));
  if (filters.search?.trim()) params.set('search', filters.search.trim());
  if (filters.page) params.set('page', String(filters.page));
  if (filters.pageSize) params.set('pageSize', String(filters.pageSize));
  return apiJson<PortalGalleryResponse>(`/api/portal/gallery?${params.toString()}`);
}

export function signPortalAsset(
  assetId: number,
  variant: PortalAssetSignVariant = 'thumbnail',
): Promise<PortalAssetSignResponse> {
  return apiJson<PortalAssetSignResponse>(`/api/portal/assets/${assetId}/sign`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ variant }),
  });
}
